import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { FilterFace } from '../primitives/FilterFace';
import { DT } from '../tokens';

/**
 * Upstream challenge: aerosol generators inject into a duct → mixed
 * concentration measured upstream of the HEPA/ULPA filter.
 */
export function DiagramCleanroomUpstreamChallenge() {
  const ductX = 28;
  const ductY = 105;
  const ductW = 260;
  const ductH = 42;
  const ductCY = ductY + ductH / 2;

  // Generator positions (3 nozzle/jet symbols, evenly spaced)
  const genXs = [72, 112, 152];
  const genY = 70;       // centre of generator body
  const genR = 7;        // radius of generator symbol

  // Filter face
  const filterX = ductX + ductW;
  const filterY = ductY - 10;
  const filterH = ductH + 20;

  // Aerosol spray particles between generators and filter
  const particles: [number, number][] = [
    [175, 112], [190, 130], [200, 118], [215, 138], [225, 110],
    [238, 125], [250, 115], [260, 133], [272, 120], [248, 140],
    [185, 142], [210, 107], [230, 140],
  ];

  return (
    <DiagramCanvas>
      {/* Duct body */}
      <Rect x={ductX} y={ductY} width={ductW} height={ductH}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />

      {/* Inlet airflow arrow from left */}
      <Line x1={8} y1={ductCY} x2={ductX - 2} y2={ductCY}
        stroke={DT.arrow} strokeWidth="1.5" />
      <Polygon
        points={`${ductX - 2},${ductCY} ${ductX - 9},${ductCY - 4} ${ductX - 9},${ductCY + 4}`}
        fill={DT.arrow} />
      <SvgText x={14} y={ductCY - 6} fill={DT.desc} fontSize="7" fontFamily={DT.mono}>Q</SvgText>

      {/* Airflow arrow inside duct */}
      <Line x1={ductX + 8} y1={ductCY} x2={ductX + 45} y2={ductCY}
        stroke={DT.arrow} strokeWidth="1" opacity={0.5} />
      <Polygon
        points={`${ductX + 45},${ductCY} ${ductX + 39},${ductCY - 3} ${ductX + 39},${ductCY + 3}`}
        fill={DT.arrow} opacity={0.5} />

      {/* Aerosol generators */}
      {genXs.map((gx, i) => (
        <G key={i}>
          {/* Generator body (circle = nozzle/jet) */}
          <Circle cx={gx} cy={genY} r={genR}
            fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
          <SvgText x={gx} y={genY + 3} fill={DT.varColor} fontSize="6"
            fontFamily={DT.mono} textAnchor="middle">
            {i + 1}
          </SvgText>
          {/* Injection line from generator to duct top */}
          <Line x1={gx} y1={genY + genR} x2={gx} y2={ductY}
            stroke={DT.varColor} strokeWidth="1" strokeDasharray="3,2" />
          {/* Spray dot at injection point */}
          <Circle cx={gx} cy={ductY + 3} r={2.5} fill={DT.varColor} opacity={0.6} />
        </G>
      ))}

      {/* Label: N above generators */}
      <SvgText x={genXs[0] - 14} y={genY - 13} fill={DT.varColor}
        fontSize="8" fontFamily={DT.mono} fontWeight="700">N jets</SvgText>

      {/* Aerosol cloud particles (challenge zone) */}
      {particles.map(([px, py], i) => (
        <Circle key={i} cx={px} cy={py} r={1.4} fill={DT.varColor} opacity={0.35} />
      ))}

      {/* Concentration badge (upstream challenge) */}
      <Rect x={190} y={ductY + 8} width={62} height={18}
        fill="rgba(0,0,0,0.5)" stroke={DT.varColor} strokeWidth="0.7" rx={3} />
      <SvgText x={221} y={ductY + 21} fill={DT.varColor}
        fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">
        C (μg/L)
      </SvgText>

      {/* Filter */}
      <FilterFace x={filterX} y={filterY} width={12} height={filterH} showArrows={false} />
      <SvgText x={filterX + 6} y={filterY - 5} fill={DT.desc}
        fontSize="7" fontFamily={DT.mono} textAnchor="middle">HEPA</SvgText>

      {/* Clean (downstream) arrow */}
      <Line x1={filterX + 12} y1={ductCY} x2={filterX + 32} y2={ductCY}
        stroke={DT.arrow} strokeWidth="1.5" />
      <Polygon
        points={`${filterX + 32},${ductCY} ${filterX + 26},${ductCY - 4} ${filterX + 26},${ductCY + 4}`}
        fill={DT.arrow} />
      <SvgText x={filterX + 14} y={ductCY - 6} fill={DT.desc}
        fontSize="7" fontFamily={DT.mono}>clean</SvgText>

      {/* Annotations */}
      <LeaderLine
        x1={genXs[1]} y1={genY - genR}
        x2={20} y2={36}
        varName="N" desc="# of jets/nozzles"
        anchor="start"
      />
      <LeaderLine
        x1={ductX + 20} y1={ductCY}
        x2={20} y2={228}
        varName="Q" desc="airflow (CFM)"
        anchor="start"
      />
      <LeaderLine
        x1={221} y1={ductY + 8}
        x2={330} y2={50}
        varName="C" desc="μg/L upstream"
        anchor="end"
      />

      <FormulaTag formula="C = (N × factor) / Q    [nozzle ×13,500 · jet ×3,375]" />
    </DiagramCanvas>
  );
}
